// The AEAT register: its configuration, and what has been filed.
//
// Both on one page, the way Backups puts the schedule next to the run history,
// and for the same reason — the configuration only means something alongside the
// evidence that it is working. A register that quietly stopped filing three weeks
// ago is worse than one that was never switched on, so the queue counts sit at the
// top of the page where they cannot be missed.
//
// The records themselves are shown and never edited. There is no control here
// that changes one, because the chain is append-only: a record is superseded or
// withdrawn, both of which happen on the invoice, not here.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import {
  Checkbox, EmptyState, ErrorAlert, LoadingRow, SelectField, TextField,
} from '../components/ui';
import { formatDate } from '@shared/format';
import {
  VERIFACTU_REGIME_KEYS, VERIFACTU_ZERO_RATE_CODES,
  type VerifactuCertificateType, type VerifactuEnvironment, type VerifactuRecord,
  type VerifactuRecordStatus, type VerifactuRegimeKey, type VerifactuSettings,
  type VerifactuSettingsInput, type VerifactuZeroRateCode,
} from '@shared/types';
import type { TranslationKey } from '@shared/i18n/index';

const ZERO_RATE_LABELS: Record<VerifactuZeroRateCode, TranslationKey> = {
  E1: 'verifactu.zeroRateE1', E2: 'verifactu.zeroRateE2', E3: 'verifactu.zeroRateE3',
  E4: 'verifactu.zeroRateE4', E5: 'verifactu.zeroRateE5', E6: 'verifactu.zeroRateE6',
  N1: 'verifactu.zeroRateN1', N2: 'verifactu.zeroRateN2',
};

const STATUS_LABELS: Record<VerifactuRecordStatus, TranslationKey> = {
  pending: 'verifactu.status.pending',
  accepted: 'verifactu.status.accepted',
  warned: 'verifactu.status.warned',
  rejected: 'verifactu.status.rejected',
  failed: 'verifactu.status.failed',
};

/** Reuses the badge colours the invoice status already establishes. */
const STATUS_TONE: Record<VerifactuRecordStatus, string> = {
  pending: 'badge--pending',
  accepted: 'badge--paid',
  warned: 'badge--pending',
  rejected: 'badge--danger',
  failed: 'badge--danger',
};

export function RecordStatusBadge({ status }: { status: VerifactuRecordStatus }) {
  const t = useT();
  return <span className={`badge ${STATUS_TONE[status]}`}>{t(STATUS_LABELS[status])}</span>;
}

export function Verifactu() {
  const t = useT();
  const navigate = useNavigate();
  const settings = useAsync(() => api.verifactuSettings(), []);
  const queue = useAsync(() => api.verifactuQueue(), []);
  const records = useAsync(() => api.verifactuRecords({ limit: 100 }), []);

  if (settings.loading && !settings.data) return <LoadingRow />;
  // The commonest error here is the upgrade not having been run, which the
  // message says in so many words.
  if (settings.error) return <ErrorAlert message={settings.error} onRetry={settings.reload} />;
  if (!settings.data) return null;

  const reloadAll = () => { settings.reload(); queue.reload(); records.reload(); };

  return (
    <>
      <div className="page-head">
        <div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/settings')}>
            <Icon name="arrow-left" size={14} />{t('nav.settings')}
          </button>
          <h1 className="page-head__title" style={{ marginTop: 6 }}>{t('verifactu.title')}</h1>
          <p className="page-head__subtitle">{t('verifactu.subtitle')}</p>
        </div>
      </div>

      {queue.data && queue.data.chainLength > 0 && (
        <div className="stats">
          <div className="stat">
            <div className="stat__label">{t('verifactu.chainLength')}</div>
            <div className="stat__value">{queue.data.chainLength}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('verifactu.queueAccepted')}</div>
            <div className="stat__value">{queue.data.accepted}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('verifactu.queuePending')}</div>
            <div className={`stat__value${queue.data.pending > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
              {queue.data.pending}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('verifactu.queueRejected')}</div>
            <div className={`stat__value${queue.data.rejected > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
              {queue.data.rejected}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('verifactu.queueFailed')}</div>
            <div className={`stat__value${queue.data.failed > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
              {queue.data.failed}
            </div>
          </div>
        </div>
      )}

      <VerifactuForm initial={settings.data} onSaved={reloadAll} />
      <Register records={records} onSent={reloadAll} />
    </>
  );
}

function VerifactuForm({ initial, onSaved }: {
  initial: VerifactuSettings;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [form, setForm] = useState(initial);
  // Write-only: blank means "leave the stored password alone".
  const [password, setPassword] = useState('');
  const [certPassword, setCertPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  // AEAT's own words about a refusal, kept on screen rather than in a toast:
  // it is what the next correction is made from.
  const [testDetail, setTestDetail] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => { setForm(initial); }, [initial]);

  const set = <K extends keyof VerifactuSettings>(key: K, value: VerifactuSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const payload = (): VerifactuSettingsInput => ({
    enabled: form.enabled,
    environment: form.environment,
    certificateType: form.certificateType,
    issuerNif: form.issuerNif,
    issuerName: form.issuerName,
    seriesPrefix: form.seriesPrefix,
    zeroRateCode: form.zeroRateCode,
    regimeKey: form.regimeKey,
    systemName: form.systemName,
    systemId: form.systemId,
    systemVersion: form.systemVersion,
    installationId: form.installationId,
    producerName: form.producerName,
    producerNif: form.producerNif,
    autoSubmit: form.autoSubmit,
    ...(password ? { certificatePassword: password } : {}),
  });

  async function save(event?: FormEvent): Promise<boolean> {
    event?.preventDefault();
    setSaving(true);
    setErrors({});
    setFormError(undefined);
    try {
      await api.updateVerifactuSettings(payload());
      setPassword('');
      onSaved();
      return true;
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function uploadCertificate(file: File) {
    if (!certPassword) {
      setErrors({ certificate: t('error.required') });
      return;
    }
    setUploading(true);
    setErrors({});
    try {
      await api.uploadVerifactuCertificate(file, certPassword);
      setCertPassword('');
      if (fileInput.current) fileInput.current.value = '';
      toast.success(t('verifactu.certificateUploaded'));
      onSaved();
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(Object.keys(fields).length > 0 ? fields : { certificate: toMessage(err) });
    } finally {
      setUploading(false);
    }
  }

  async function removeCertificate() {
    if (!window.confirm(t('verifactu.certificateRemoveConfirm'))) return;
    setUploading(true);
    try {
      await api.deleteVerifactuCertificate();
      onSaved();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setUploading(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestDetail(undefined);
    try {
      // Saved first: the test uses the stored certificate and the stored
      // environment, not what is on screen.
      if (!await save()) return;
      const result = await api.testVerifactu();
      if (result.ok) {
        toast.success(t('verifactu.testOk', { detail: result.detail ?? '' }));
      } else {
        setTestDetail(result.detail ?? '');
        toast.error(t('verifactu.testFailed', { detail: result.detail ?? '' }));
      }
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setTesting(false);
    }
  }

  const expired = form.certificateDaysLeft !== null && form.certificateDaysLeft < 0;
  const expiringSoon = form.certificateDaysLeft !== null
    && form.certificateDaysLeft >= 0 && form.certificateDaysLeft <= 30;

  return (
    <form onSubmit={save} noValidate className="stack" style={{ marginTop: 16 }}>
      {formError && <ErrorAlert message={formError} />}

      {form.problems.length > 0 && (
        <div className="alert alert--warning">
          <Icon name="alert-circle" />
          <div>
            <strong>{t('verifactu.problems')}</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {form.problems.map((problem) => (
                <li key={problem}>{t(problem as TranslationKey)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">{t('verifactu.connection')}</h2>
          <span className="muted">{form.environment === 'production'
            ? t('verifactu.environmentProduction')
            : t('verifactu.environmentSandbox')}</span>
        </div>
        <div className="card__body stack">
          <Checkbox label={t('verifactu.enabled')} checked={form.enabled}
            onChange={(v) => set('enabled', v)} />
          <p className="field__hint">
            {form.enabled ? t('verifactu.onNote') : t('verifactu.disabledNote')}
          </p>

          <div className="form-grid form-grid--2">
            <SelectField label={t('verifactu.environment')} hint={t('verifactu.environmentHint')}
              wrapperClassName="span-all" value={form.environment} error={errors.environment}
              onChange={(e) => set('environment', e.target.value as VerifactuEnvironment)}>
              <option value="sandbox">{t('verifactu.environmentSandbox')}</option>
              <option value="production">{t('verifactu.environmentProduction')}</option>
            </SelectField>

            <SelectField label={t('verifactu.certificateType')} hint={t('verifactu.certificateTypeHint')}
              wrapperClassName="span-all" value={form.certificateType} error={errors.certificateType}
              onChange={(e) => set('certificateType', e.target.value as VerifactuCertificateType)}>
              <option value="certificate">{t('verifactu.certificateTypeCertificate')}</option>
              <option value="seal">{t('verifactu.certificateTypeSeal')}</option>
            </SelectField>
          </div>

          <Checkbox label={t('verifactu.autoSubmit')} checked={form.autoSubmit}
            onChange={(v) => set('autoSubmit', v)} />
          <p className="field__hint">{t('verifactu.autoSubmitHint')}</p>
        </div>
        <div className="card__foot">
          <div className="row">
            <button type="button" className="btn" onClick={() => void test()}
              disabled={testing || saving || !form.hasCertificate}>
              {testing ? <span className="spinner" /> : <Icon name="check-circle" />}
              {testing ? t('verifactu.testing') : t('verifactu.test')}
            </button>
            <span className="field__hint">{t('verifactu.testHint')}</span>
          </div>
        </div>
      </div>

      {testDetail !== undefined && (
        <ErrorAlert message={t('verifactu.testFailed', { detail: testDetail })} />
      )}

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('verifactu.certificate')}</h2></div>
        <div className="card__body stack">
          {expired && (
            <div className="alert alert--warning">
              <Icon name="alert-circle" /><div>{t('verifactu.certificateHasExpired')}</div>
            </div>
          )}
          {expiringSoon && (
            <div className="alert alert--info">
              <Icon name="alert-circle" />
              <div>{t('verifactu.certificateExpiresSoon', { days: form.certificateDaysLeft ?? 0 })}</div>
            </div>
          )}

          {form.hasCertificate ? (
            <div className="stack" style={{ gap: 2 }}>
              <div className="table__primary">{form.certificateFilename}</div>
              {form.certificateSubject && (
                <div className="table__meta">{t('verifactu.certificateStored', { subject: form.certificateSubject })}</div>
              )}
              {form.certificateIssuer && (
                <div className="table__meta">{t('verifactu.certificateIssuedBy', { issuer: form.certificateIssuer })}</div>
              )}
              {form.certificateNotAfter && (
                <div className="table__meta">
                  {t('verifactu.certificateExpires', { date: formatDate(form.certificateNotAfter, locale) })}
                </div>
              )}
            </div>
          ) : (
            <p className="muted">{t('verifactu.certificateNone')}</p>
          )}

          <div className="form-grid form-grid--2">
            <TextField label={t('verifactu.certificatePassword')} type="password"
              autoComplete="new-password" wrapperClassName="span-all"
              hint={form.hasCertificate
                ? t('verifactu.certificatePasswordStored')
                : t('verifactu.certificatePasswordHint')}
              value={form.hasCertificate ? password : certPassword}
              error={errors.certificate ?? errors.password}
              onChange={(e) => (form.hasCertificate
                ? setPassword(e.target.value)
                : setCertPassword(e.target.value))} />
          </div>
          <p className="field__hint">{t('verifactu.certificateFileHint')}</p>
        </div>
        <div className="card__foot">
          <div className="row">
            <label className="btn">
              <input ref={fileInput} type="file" accept=".pfx,.p12" hidden disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // A second upload replaces the first; the password typed above
                  // is the one checked against it.
                  if (file) void uploadCertificate(file);
                }} />
              {uploading ? <span className="spinner" /> : <Icon name="key" />}
              {uploading ? t('verifactu.certificateUploading') : t('verifactu.certificateUpload')}
            </label>
            {form.hasCertificate && (
              <button type="button" className="btn btn--ghost" disabled={uploading}
                onClick={() => void removeCertificate()}>
                <Icon name="trash" />{t('verifactu.certificateRemove')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('verifactu.issuer')}</h2></div>
        <div className="card__body stack">
          <div className="form-grid form-grid--2">
            <TextField label={t('verifactu.issuerNif')} optional hint={t('verifactu.issuerNifHint')}
              placeholder="B12345678" autoComplete="off"
              value={form.issuerNif} error={errors.issuerNif}
              onChange={(e) => set('issuerNif', e.target.value)} />
            <TextField label={t('verifactu.issuerName')} optional hint={t('verifactu.issuerNameHint')}
              value={form.issuerName} error={errors.issuerName}
              onChange={(e) => set('issuerName', e.target.value)} />
          </div>
          {form.effectiveIssuerNif && (
            <p className="field__hint">
              {t('verifactu.effectiveIssuer',
                { name: form.effectiveIssuerName, nif: form.effectiveIssuerNif })}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('verifactu.numbering')}</h2></div>
        <div className="card__body stack">
          <div className="form-grid form-grid--2">
            <TextField label={t('verifactu.seriesPrefix')} optional wrapperClassName="span-all"
              hint={t('verifactu.seriesPrefixHint')} placeholder="FA2026/" autoComplete="off"
              value={form.seriesPrefix} error={errors.seriesPrefix}
              onChange={(e) => set('seriesPrefix', e.target.value)} />
          </div>
          <p className="field__hint">
            {t('verifactu.seriesExample', { example: `${form.seriesPrefix}123` })}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('verifactu.tax')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <SelectField label={t('verifactu.regimeKey')} hint={t('verifactu.regimeKeyHint')}
              value={form.regimeKey} error={errors.regimeKey}
              onChange={(e) => set('regimeKey', e.target.value as VerifactuRegimeKey)}>
              {VERIFACTU_REGIME_KEYS.map((key) => (
                <option key={key} value={key}>{key}</option>
              ))}
            </SelectField>
            <SelectField label={t('verifactu.zeroRateCode')} hint={t('verifactu.zeroRateCodeHint')}
              value={form.zeroRateCode} error={errors.zeroRateCode}
              onChange={(e) => set('zeroRateCode', e.target.value as VerifactuZeroRateCode)}>
              {VERIFACTU_ZERO_RATE_CODES.map((code) => (
                <option key={code} value={code}>{t(ZERO_RATE_LABELS[code])}</option>
              ))}
            </SelectField>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">{t('verifactu.system')}</h2>
          <span className="muted">{t('verifactu.systemHint')}</span>
        </div>
        <div className="card__body stack">
          <div className="form-grid form-grid--2">
            <TextField label={t('verifactu.systemName')} value={form.systemName}
              error={errors.systemName} onChange={(e) => set('systemName', e.target.value)} />
            <TextField label={t('verifactu.systemVersion')} value={form.systemVersion}
              error={errors.systemVersion} onChange={(e) => set('systemVersion', e.target.value)} />
            <TextField label={t('verifactu.systemId')} hint={t('verifactu.systemIdHint')}
              value={form.systemId} error={errors.systemId}
              onChange={(e) => set('systemId', e.target.value)} />
            <TextField label={t('verifactu.installationId')} value={form.installationId}
              error={errors.installationId} onChange={(e) => set('installationId', e.target.value)} />
            <TextField label={t('verifactu.producerName')} optional value={form.producerName}
              error={errors.producerName} onChange={(e) => set('producerName', e.target.value)} />
            <TextField label={t('verifactu.producerNif')} optional value={form.producerNif}
              error={errors.producerNif} onChange={(e) => set('producerNif', e.target.value)} />
            <p className="field__hint span-all">{t('verifactu.producerHint')}</p>
          </div>
        </div>
        <div className="card__foot">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? <span className="spinner" /> : <Icon name="save" />}
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </div>
    </form>
  );
}

function Register({ records, onSent }: {
  records: ReturnType<typeof useAsync<VerifactuRecord[]>>;
  onSent: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const [status, setStatus] = useState<VerifactuRecordStatus | ''>('');
  const [sending, setSending] = useState<number>();

  async function send(id: number) {
    setSending(id);
    try {
      await api.sendVerifactuRecord(id);
      toast.success(t('verifactu.sent'));
      onSent();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setSending(undefined);
    }
  }

  const shown = (records.data ?? []).filter((r) => status === '' || r.status === status);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card__head">
        <div>
          <h2 className="card__title">{t('verifactu.register')}</h2>
          <span className="muted">{t('verifactu.registerSubtitle')}</span>
        </div>
        <select className="select" style={{ width: 'auto' }} value={status}
          onChange={(e) => setStatus(e.target.value as VerifactuRecordStatus | '')}
          aria-label={t('common.status')}>
          <option value="">{t('verifactu.filterAll')}</option>
          {(Object.keys(STATUS_LABELS) as VerifactuRecordStatus[]).map((value) => (
            <option key={value} value={value}>{t(STATUS_LABELS[value])}</option>
          ))}
        </select>
      </div>

      {records.error ? (
        <div className="card__body"><ErrorAlert message={records.error} onRetry={records.reload} /></div>
      ) : records.loading && !records.data ? (
        <LoadingRow />
      ) : shown.length === 0 ? (
        <EmptyState icon="inbox" title={t('verifactu.noRecords')} hint={t('verifactu.noRecordsHint')} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="shrink">{t('invoices.number')}</th>
                <th className="shrink">{t('common.status')}</th>
                <th>{t('verifactu.recordAt')}</th>
                <th>{t('verifactu.csv')}</th>
                <th className="shrink">{t('verifactu.chainHead')}</th>
                <th className="shrink" />
              </tr>
            </thead>
            <tbody>
              {shown.map((record) => (
                <tr key={record.id}>
                  <td className="shrink mono nowrap">
                    {record.invoiceId !== null ? (
                      <Link to={`/invoices/${record.invoiceId}`} className="table__primary">
                        {record.seriesNumber}
                      </Link>
                    ) : record.seriesNumber}
                    <div className="table__meta">{t(`verifactu.type.${record.recordType}`)}</div>
                  </td>
                  <td className="shrink">
                    <RecordStatusBadge status={record.status} />
                    {record.subsanacion && (
                      <div className="table__meta">{t('verifactu.subsanacion')}</div>
                    )}
                  </td>
                  <td>
                    <div className="table__meta nowrap">{record.recordAt}</div>
                    {record.errorMessage && (
                      <div className="table__meta">
                        {record.errorCode && <strong>{record.errorCode} · </strong>}
                        {record.errorMessage}
                      </div>
                    )}
                    {record.status === 'pending' && record.attempts > 0 && (
                      <div className="table__meta">
                        {t('verifactu.attempts', { count: record.attempts })}
                        {record.nextAttemptAt
                          && ` · ${t('verifactu.nextAttempt', { date: formatDate(record.nextAttemptAt, locale) })}`}
                      </div>
                    )}
                  </td>
                  <td className="mono">{record.csv || '—'}</td>
                  {/* Enough of the fingerprint to compare two records by eye;
                      the whole of it is in the record AEAT holds. */}
                  <td className="shrink mono table__meta">{record.hash.slice(0, 12)}…</td>
                  <td className="shrink">
                    {(record.status === 'pending' || record.status === 'failed') && (
                      <button type="button" className="btn btn--ghost btn--sm"
                        disabled={sending === record.id}
                        onClick={() => void send(record.id)}>
                        {sending === record.id ? <span className="spinner" /> : <Icon name="send" />}
                        {sending === record.id ? t('verifactu.sending') : t('verifactu.send')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
